import { LiveClock } from './LiveClock';
// Live football drive: who has the ball, down & distance, red-zone flag. Highlighted when it's your team.
export default function Possession({ leg, compact = false }) {
  const p = leg.possession;
  if (!p || !p.abbr) return null;
  const mine = leg.picked_side && p.side === leg.picked_side;
  return (
    <div className={`flex items-center gap-1 truncate ${compact ? 'text-[9px]' : 'text-[11px] mt-1'} ${mine ? 'text-emerald-300' : 'text-zinc-300'}`}
      data-testid={`leg-possession-${leg.id}`} title={`${p.abbr} has the ball${p.down_distance ? ` · ${p.down_distance}` : ''}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${mine ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`} />
      <b>{p.abbr} ball</b>
      {p.down_distance && <span className="truncate opacity-90">· {p.down_distance}</span>}
      {p.red_zone && <span className="shrink-0 rounded bg-red-600 px-1 text-[8px] font-black text-white">RED ZONE</span>}
    </div>
  );
}

const isLive = (l) => l.home_abbr && l.period_num && !/final/i.test(l.live_period || '');
const FOOTBALL = ['nfl', 'cfb'];

export const inRedZone = (l) => FOOTBALL.includes(String(l.league || '').toLowerCase()) && isLive(l) && !!l.possession?.red_zone;

// Solid red bar inside the leg while either team is inside the 20.
export const RedZoneBar = ({ leg }) => {
  if (!inRedZone(leg)) return null;
  const p = leg.possession;
  return (
    <div className="mt-0.5 rounded bg-red-600 px-1 text-[9px] font-black leading-4 text-white truncate shadow-[0_0_8px_rgba(239,68,68,0.7)]"
      data-testid={`red-zone-bar-${leg.id}`}>
      RED ZONE · {p.abbr} {p.short || p.down_distance || ''}{p.spot ? ` at ${p.spot}` : ''}
    </div>
  );
};

export const ticketInRedZone = (ticket) => (ticket.legs || []).some(inRedZone);

// "HOU 31-28 JAX · 2:31 - 4th · JAX 1st & 10 at HOU 48" for one leg's game.
export const LiveLine = ({ leg, className = 'text-[9px]', testid, stacked = false }) => {
  if (!isLive(leg)) return null;
  const p = leg.possession;
  if (stacked) {
    return (
      <div className={`font-mono leading-tight rounded bg-black/40 px-1 py-0.5 ${className}`} data-testid={testid || `leg-live-${leg.id}`}>
        <p className="font-bold text-white">{leg.away_abbr} {leg.live_score_away}-{leg.live_score_home} {leg.home_abbr}</p>
        <p className="text-zinc-300"><LiveClock leg={leg} testid={`leg-live-clock-${leg.id}`} /></p>
        {p?.short && <p className="text-amber-200">{p.abbr} ball · {p.short}{p.spot ? ` at ${p.spot}` : ''}{p.red_zone ? ' · RZ' : ''}</p>}
      </div>
    );
  }
  return (
    <div className={`font-mono font-bold text-white/90 truncate ${className}`} data-testid={testid || `leg-live-${leg.id}`}>
      {leg.away_abbr} {leg.live_score_away}-{leg.live_score_home} {leg.home_abbr}
      <span className="font-normal opacity-80"> · <LiveClock leg={leg} testid={`leg-live-clock-${leg.id}`} /></span>
      {p?.short && <span className="text-amber-200"> · {p.abbr} {p.short}{p.spot ? ` at ${p.spot}` : ''}</span>}
    </div>
  );
};

// One line per live game on a ticket (deduped by game).
export const LiveGames = ({ ticket, max = 3 }) => {
  const seen = new Set();
  const games = (ticket.legs || []).filter((l) => isLive(l) && !seen.has(l.espn_event_id) && seen.add(l.espn_event_id));
  if (!games.length) return null;
  return (
    <div className="space-y-px" data-testid={`tile-live-${ticket.id}`}>
      {games.slice(0, max).map((l) => <LiveLine key={l.espn_event_id} leg={l} stacked className="text-[8px]" testid={`tile-live-${ticket.id}-${l.espn_event_id}`} />)}
      {games.length > max && <p className="text-[8px] text-zinc-500">+{games.length - max} more live</p>}
    </div>
  );
};
